import { useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, X, ChevronDown } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import LocalizedLink from "@/components/LocalizedLink";
import { buildLocalizedPath } from "@/i18n/detectLanguage";
import { getApplicationCanonicalPath } from "@/i18n/legacySeoPaths";
import { ProfilePanel } from "@/components/portal/ProfilePanel";
import { useAuth } from "@/hooks/useAuth";
import logo from "@/assets/logo-thailand-elite-transparent.png";

const Header = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState<string | null>(null);
  const location = useLocation();
  const applicationGuidePath = getApplicationCanonicalPath(i18n.language);

  const privilegeLinks = [
    { to: "/visa", label: t("nav.eliteVisa") },
    { to: "/privilege-points", label: t("nav.privilegePoints") },
  ];

  const membershipLinks = [
    { to: "/membership/bronze", label: t("nav.bronze") },
    { to: "/membership/gold", label: t("nav.gold") },
    { to: "/membership/platinum", label: t("nav.platinum") },
    { to: "/membership/diamond", label: t("nav.diamond") },
    { to: "/membership/reserve", label: t("nav.reserve") },
    { to: "/family", label: t("nav.family") },
    { to: "/compare", label: t("nav.compare") },
  ];

  const navItems = [
    {
      to: "/privileges",
      label: t("nav.privileges"),
      children: privilegeLinks,
    },
    {
      to: "/memberships",
      label: t("nav.memberships"),
      children: membershipLinks,
    },
    {
      to: "/guides",
      label: t("nav.guides"),
      children: [
        { to: applicationGuidePath, label: t("nav.howToApply") },
        { to: "/faqs", label: t("nav.faqs") },
        { to: "/blog", label: t("nav.blog", "Articles") },
      ],
    },
    { to: "/about", label: t("nav.about") },
    { to: "/contact", label: t("nav.contact") },
  ];

  const isActive = (path: string) => {
    const localized = buildLocalizedPath(path, i18n.language);
    return location.pathname === localized;
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-md border-b border-border">
      {/* Top bar */}
      <div className="bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 py-1.5 flex items-center justify-between text-xs font-body">
          <span className="hidden sm:inline">{t("hero.badge")}</span>
          <div className="flex items-center gap-3 sm:gap-6 mx-auto sm:mx-0">
            <span>🔒 {t("hero.secure")}</span>
            <span className="hidden md:inline">🌍 {t("hero.support")}</span>
            <span className="hidden lg:inline">🛡️ {t("hero.noFees")}</span>
            <span>✅ {t("hero.payAfter")}</span>
          </div>
        </div>
      </div>

      {/* Main nav */}
      <nav className="container mx-auto px-4 flex items-center justify-between h-16 md:h-20">
        <LocalizedLink to="/" className="flex-shrink-0">
          <img
            src={logo}
            alt="Thailand Elite"
            className="h-12 md:h-14 w-auto"
            width={320}
            height={164}
            fetchPriority="high"
            decoding="async"
          />
        </LocalizedLink>

        {/* Desktop nav */}
        <div className="hidden lg:flex items-center gap-1">
          {navItems.map((item) => (
            <div
              key={item.to}
              className="relative"
              onMouseEnter={() => item.children && setDropdownOpen(item.to)}
              onMouseLeave={() => setDropdownOpen(null)}
            >
              <LocalizedLink
                to={item.to}
                className={`px-3 py-2 text-sm font-body font-medium transition-colors flex items-center gap-1 rounded-md
                  ${isActive(item.to) ? "text-accent" : "text-foreground/80 hover:text-accent"}`}
              >
                {item.label}
                {item.children && <ChevronDown className="h-3 w-3" />}
              </LocalizedLink>

              {item.children && dropdownOpen === item.to && (
                <motion.div
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="absolute top-full left-0 bg-card shadow-elegant rounded-lg border border-border py-2 min-w-[220px]"
                >
                  {item.children.map((child) => (
                    <LocalizedLink
                      key={child.to}
                      to={child.to}
                      className="block px-4 py-2 text-sm font-body text-foreground/80 hover:text-accent hover:bg-secondary transition-colors"
                    >
                      {child.label}
                    </LocalizedLink>
                  ))}
                </motion.div>
              )}
            </div>
          ))}
        </div>

        <div className="hidden lg:flex items-center gap-4">
          <LanguageSwitcher />
          {user && <ProfilePanel />}
          <LocalizedLink
            to="/apply"
            className="px-5 py-2.5 bg-accent text-accent-foreground font-body font-semibold text-sm rounded-md hover:opacity-90 transition-opacity shadow-gold"
          >
            {t("nav.applyOnline")}
          </LocalizedLink>
        </div>

        {/* Mobile toggle */}
        <div className="flex lg:hidden items-center gap-3">
          <LanguageSwitcher />
          {user && <ProfilePanel />}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="p-2 text-foreground"
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden border-t border-border bg-background overflow-hidden"
          >
            <div className="container mx-auto px-4 py-4 flex flex-col gap-1">
              {navItems.map((item) => (
                <div key={item.to}>
                  <LocalizedLink
                    to={item.to}
                    onClick={() => setMobileOpen(false)}
                    className="block px-3 py-2.5 text-sm font-body font-medium text-foreground/80 hover:text-accent transition-colors"
                  >
                    {item.label}
                  </LocalizedLink>
                  {item.children && (
                    <div className="pl-6">
                      {item.children.map((child) => (
                        <LocalizedLink
                          key={child.to}
                          to={child.to}
                          onClick={() => setMobileOpen(false)}
                          className="block px-3 py-2 text-xs font-body text-muted-foreground hover:text-accent transition-colors"
                        >
                          {child.label}
                        </LocalizedLink>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <LocalizedLink
                to="/apply"
                onClick={() => setMobileOpen(false)}
                className="mt-3 px-5 py-2.5 bg-accent text-accent-foreground font-body font-semibold text-sm rounded-md text-center shadow-gold"
              >
                {t("nav.applyOnline")}
              </LocalizedLink>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
